import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import PublicHeader from '../../components/self-service/PublicHeader';
import SignInModal from '../../components/self-service/auth/SignInModal';
import { useAuth } from '../../components/self-service/auth/useAuth';
import Banner from '../../components/self-service/shared/Banner';
import { formatDate, formatDateTime } from '../../components/self-service/shared/dateFormat';
import { apiClient } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { usePublisherConfig } from '../../data/publisherConfigs';
import type { SelfServiceMonthlyPaymentRecovery, SelfServiceSubmissionRow } from '../../types/selfService';
import StripePaymentSection, { StripePaymentActionSection } from '../../components/self-service/StripePaymentSection';

interface PaymentRecoverySetupResponse {
  clientSecret: string;
  setupIntentId: string;
  amountCents: number;
  paymentPlan: 'upfront' | 'monthly';
  installment: SelfServiceMonthlyPaymentRecovery | null;
}

interface PaymentRecoveryRetryResponse {
  status: 'requires_action' | 'processing' | 'payment_authorized' | 'approved';
  clientSecret?: string;
  paymentIntentId?: string;
  row?: SelfServiceSubmissionRow;
}

const dollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const isRecoveryExpiredMessage = (message: string) =>
  message.includes('7-day update window') || message.includes('No monthly payment is currently');

const formatTimeRemaining = (ms: number): string => {
  if (ms <= 0) return 'Expired';
  const totalMinutes = Math.ceil(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes} min remaining`;
  if (minutes === 0) return `${hours} hr remaining`;
  return `${hours} hr ${minutes} min remaining`;
};

const UpdatePaymentPage: React.FC = () => {
  const { slug, submissionId } = useParams<{ slug: string; submissionId: string }>();
  const effectiveSlug = slug ?? '';
  const effectiveSubmissionId = submissionId ?? '';
  const { user, isAuthenticated, selectedAdvertiser, advertiserMemberships } = useAuth();
  const { config: cfg } = usePublisherConfig(effectiveSlug);
  const [signInOpen, setSignInOpen] = React.useState(!isAuthenticated);
  const [row, setRow] = React.useState<SelfServiceSubmissionRow | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [setup, setSetup] = React.useState<PaymentRecoverySetupResponse | null>(null);
  const [setupBusy, setSetupBusy] = React.useState(false);
  const [setupStartedFor, setSetupStartedFor] = React.useState<string | null>(null);
  const [retryBusy, setRetryBusy] = React.useState(false);
  const [recoveryAction, setRecoveryAction] = React.useState<{ clientSecret: string; paymentIntentId: string } | null>(null);
  const [recoveryError, setRecoveryError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [resultStatus, setResultStatus] = React.useState<'success' | 'processing' | null>(null);
  const [forceExpired, setForceExpired] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => { setSignInOpen(!isAuthenticated); }, [isAuthenticated]);

  React.useEffect(() => {
    const advertiserId = selectedAdvertiser?.advertiserId;
    if (!advertiserId || !submissionId) {
      if (isAuthenticated && advertiserMemberships === null) {
        setLoading(true);
        return;
      }
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .post<{ rows: SelfServiceSubmissionRow[] }>('/submissions/list-by-advertiser', {
        advertiserId,
        stationSlug: slug,
      })
      .then(({ rows }) => {
        if (cancelled) return;
        setRow((rows ?? []).find((r) => r.id === submissionId) ?? null);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load campaign');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [advertiserMemberships, isAuthenticated, selectedAdvertiser?.advertiserId, slug, submissionId]);

  const canRecover = !!row && row.payment_plan === 'monthly' && !!row.monthlyPaymentRecovery;
  const accent = cfg?.accentColor || cfg?.primaryColor || '#2563eb';
  const publisherName = cfg?.name ?? slug ?? 'your publisher';
  const campaignName = row?.campaign?.campaignName?.trim() || row?.business_info?.businessName || 'Campaign';
  const recovery = setup?.installment ?? row?.monthlyPaymentRecovery ?? null;
  const amountCents = setup?.amountCents ?? recovery?.amountCents ?? 0;
  const retryDeadlineMs = recovery?.nextAttemptAt ? Date.parse(recovery.nextAttemptAt) : NaN;
  const recoveryWindowExpired = row?.status === 'approved'
    && (forceExpired || (Number.isFinite(retryDeadlineMs) && retryDeadlineMs <= now));
  const retryTimeRemaining = Number.isFinite(retryDeadlineMs)
    ? formatTimeRemaining(retryDeadlineMs - now)
    : null;

  React.useEffect(() => {
    if (!recovery?.nextAttemptAt) return;
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, [recovery?.nextAttemptAt]);

  React.useEffect(() => {
    setForceExpired(false);
    setResultStatus(null);
  }, [row?.id]);

  const startSetup = React.useCallback(async () => {
    if (!row || !canRecover || setupBusy || recoveryWindowExpired) return;
    setSetupBusy(true);
    setRecoveryError(null);
    setSuccess(null);
    setResultStatus(null);
    setRecoveryAction(null);
    setSetupStartedFor(row.id);
    try {
      const path = `/submissions/${row.id}/payment-recovery/setup-intent`;
      const data = await apiClient.post<PaymentRecoverySetupResponse>(
        path,
        {},
      );
      setSetup(data);
      analytics.track(EVENTS.PAYMENT_RECOVERY_STARTED, { station_slug: slug, submission_id: row.id });
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to start payment recovery';
      if (isRecoveryExpiredMessage(message)) setForceExpired(true);
      setRecoveryError(message);
    } finally {
      setSetupBusy(false);
    }
  }, [canRecover, recoveryWindowExpired, row, setupBusy, slug]);

  React.useEffect(() => {
    if (!row || !canRecover || setup || setupBusy || setupStartedFor === row.id) return;
    void startSetup();
  }, [canRecover, row, setup, setupBusy, setupStartedFor, startSetup]);

  const handleRecoveryResult = (data: PaymentRecoveryRetryResponse) => {
    if (data.status === 'requires_action') {
      if (!data.clientSecret || !data.paymentIntentId) {
        setRecoveryError('Stripe requires bank verification but did not return the verification details.');
        return;
      }
      setRecoveryAction({ clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId });
      return;
    }
    if (data.row) setRow(data.row);
    setRecoveryAction(null);
    analytics.track(EVENTS.PAYMENT_RECOVERED, {
      station_slug: slug,
      submission_id: row?.id,
      status: data.status,
    });
    setResultStatus(data.status === 'processing' ? 'processing' : 'success');
    setSuccess(
      data.status === 'processing'
        ? 'Your payment is processing. We will update this campaign once Stripe confirms it.'
        : 'Payment received. This card will be used for future monthly charges on this campaign.',
    );
  };

  const retryPaymentRecovery = async (paymentIntentId?: string | null) => {
    if (!row || !setup?.setupIntentId || recoveryWindowExpired) return;
    setRetryBusy(true);
    setRecoveryError(null);
    try {
      const path = `/submissions/${row.id}/payment-recovery/retry`;
      const data = await apiClient.post<PaymentRecoveryRetryResponse>(
        path,
        {
          setupIntentId: setup.setupIntentId,
          ...(paymentIntentId ? { paymentIntentId } : {}),
        },
      );
      handleRecoveryResult(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unable to retry payment';
      if (isRecoveryExpiredMessage(message)) setForceExpired(true);
      setRecoveryError(message);
    } finally {
      setRetryBusy(false);
    }
  };

  if (!submissionId) return <Navigate to={`/${effectiveSlug}/my-campaigns`} replace />;

  if (!isAuthenticated || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 max-w-md w-full text-center">
          <CreditCard className="w-10 h-10 text-slate-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Sign in to update payment</h1>
          <p className="text-sm text-slate-600 mb-6">Sign in with your advertiser account to retry this monthly charge.</p>
          <button
            type="button"
            onClick={() => setSignInOpen(true)}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md"
          >
            Sign in
          </button>
        </div>
        <SignInModal open={signInOpen} onClose={() => setSignInOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader
        publisher={cfg ?? undefined}
        rightSlot={(
          <Link
            to={`/${effectiveSlug}/my-campaigns/${effectiveSubmissionId}`}
            className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.08em] text-slate-500 hover:text-slate-900 px-3 py-1.5 rounded-full hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Campaign
          </Link>
        )}
      />

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading payment details...
          </div>
        ) : error ? (
          <Banner tone="warn" label="Couldn't load payment details">{error}</Banner>
        ) : !row ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <AlertTriangle className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-slate-700 font-medium">Campaign not found</p>
            <p className="text-sm text-slate-500 mt-1">It may have been removed, or you may be signed in with a different advertiser.</p>
          </div>
        ) : success ? (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center shadow-sm">
            {resultStatus === 'processing' ? (
              <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            )}
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700 mb-2">
              {resultStatus === 'processing' ? 'Payment processing' : 'Payment updated'}
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">{success}</h1>
            <p className="text-sm text-slate-600 mt-3">A receipt will be sent to {row.contact_email} after the charge is confirmed.</p>
            <Link
              to={`/${effectiveSlug}/my-campaigns/${effectiveSubmissionId}`}
              className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: accent }}
            >
              View campaign
            </Link>
          </div>
        ) : !canRecover ? (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-semibold text-slate-900">No payment update needed</h1>
            <p className="text-sm text-slate-600 mt-2">This campaign does not have a recoverable monthly payment right now.</p>
            <Link
              to={`/${effectiveSlug}/my-campaigns/${effectiveSubmissionId}`}
              className="mt-5 inline-flex items-center justify-center px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ backgroundColor: accent }}
            >
              Back to campaign
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[minmax(0,1fr)_22rem] gap-5 items-start">
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
              <div className="p-6 md:p-7 border-b border-slate-100 bg-gradient-to-br from-white via-white to-rose-50/60">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-semibold uppercase tracking-[0.1em] mb-4">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Payment declined
                </div>
                <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-950">Update payment method</h1>
                <p className="text-sm md:text-base text-slate-600 mt-2 max-w-2xl">
                  Pay the missed monthly charge for {campaignName} and save this card for future monthly charges on the campaign.
                </p>
              </div>

              <div className="p-6 md:p-7 space-y-5">
                {recoveryError && <Banner tone="warn" label="Payment update failed">{recoveryError}</Banner>}

                {recoveryWindowExpired ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-950 space-y-3">
                    <div className="font-semibold">This update window has closed.</div>
                    <p>The automatic retry window ended at {recovery?.nextAttemptAt ? formatDateTime(recovery.nextAttemptAt) : 'the scheduled retry time'}. Refresh the campaign to see the current payment status.</p>
                    <button
                      type="button"
                      onClick={() => window.location.reload()}
                      className="px-4 py-2 rounded-lg bg-amber-600 text-white text-xs font-semibold uppercase tracking-[0.08em]"
                    >
                      Refresh status
                    </button>
                  </div>
                ) : setupBusy && !setup ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Preparing secure payment form...
                  </div>
                ) : recoveryAction ? (
                  <div className="space-y-3">
                    <Banner tone="caution" label="Bank verification required">
                      Your bank needs one more verification step before we can recover this payment.
                    </Banner>
                    <StripePaymentActionSection
                      clientSecret={recoveryAction.clientSecret}
                      accentColor={accent}
                      onSuccess={(id) => retryPaymentRecovery(id ?? recoveryAction.paymentIntentId)}
                      onError={(msg) => setRecoveryError(msg)}
                    />
                  </div>
                ) : setup ? (
                  <StripePaymentSection
                    clientSecret={setup.clientSecret}
                    intentType="setup"
                    accentColor={accent}
                    amountCents={amountCents}
                    submitLabel={retryBusy ? 'Charging...' : `Pay ${dollars(amountCents)} and save card`}
                    disabled={retryBusy || recoveryWindowExpired}
                    disabledReason={recoveryWindowExpired ? 'This payment update window has closed. Refresh the campaign status.' : undefined}
                    onSuccess={() => retryPaymentRecovery()}
                    onError={(msg) => setRecoveryError(msg)}
                    beforeButton={(
                      <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-[12px] text-blue-900 flex items-start gap-2">
                        <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-600 shrink-0" />
                        <span>This authorizes the missed monthly charge and stores the new card for the remaining installments.</span>
                      </div>
                    )}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={startSetup}
                    className="w-full px-6 py-3 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ backgroundColor: accent }}
                  >
                    Start secure payment update
                  </button>
                )}
              </div>
            </section>

            <aside className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Campaign</div>
                <div className="mt-1 text-lg font-semibold tracking-tight text-slate-950">{campaignName}</div>
                <div className="text-sm text-slate-500">{publisherName}</div>
              </div>
              <div className="h-px bg-slate-100" />
              <SummaryRow label="Amount due" value={amountCents > 0 ? dollars(amountCents) : 'Preparing...'} strong />
              <SummaryRow label="Payment plan" value="Monthly" />
              {recovery && (
                <>
                  <SummaryRow label="Billing period" value={`${formatDate(recovery.periodStart)} - ${formatDate(recovery.periodEnd)}`} />
                  {retryTimeRemaining && <SummaryRow label="Time remaining" value={retryTimeRemaining} />}
                </>
              )}
            </aside>
          </div>
        )}
      </main>
    </div>
  );
};

const SummaryRow: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="text-slate-500">{label}</span>
    <span className={`text-right ${strong ? 'text-base font-semibold text-slate-950' : 'font-medium text-slate-800'}`}>{value}</span>
  </div>
);

export default UpdatePaymentPage;
